/* eslint-disable no-nested-ternary -- disable eslint rule */
import type { Frame } from "@rekajs/core";
import { observer } from "@rekajs/react";
import * as t from "@rekajs/types";
import * as React from "react";

interface SelectorContextType {
  onConnect: (dom: HTMLElement, view: t.View) => (() => void) | undefined;
}

export const SelectorContext = React.createContext<SelectorContextType>(
  null as never
);

interface RenderErrorViewProps {
  view: t.ErrorSystemView;
}

const RenderErrorView = observer((props: RenderErrorViewProps) => {
  return (
    <div>
      <h4>Error: {props.view.error}</h4>
    </div>
  );
});

interface RenderComponentViewProps {
  view: t.ComponentView;
}

interface RenderExternalComponentViewProps {
  view: t.ExternalComponentView;
}

const RenderExternalComponentView = observer(
  (props: RenderExternalComponentViewProps) => {
    const componentProps = {
      ...props.view.props,
      view: props.view,
      children: props.view.children.map((child) => (
        <InternalRenderer key={child.id} view={child} />
      )),
    };

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call -- JSX children can be unsafe
    return React.cloneElement(props.view.component.render(componentProps));
  }
);

const RenderComponentView = observer((props: RenderComponentViewProps) => {
  return viewInstanceOfExternalCompView(props.view) ? (
    <RenderExternalComponentView view={props.view} />
  ) : viewInstaceOfRekaCompView(props.view) ? (
    props.view.render.map((r) => <InternalRenderer key={r.id} view={r} />)
  ) : null;
});

function viewInstaceOfRekaCompView(
  view: t.ComponentView
): view is t.RekaComponentView {
  return view instanceof t.RekaComponentView;
}

function viewInstanceOfExternalCompView(
  view: t.ComponentView
): view is t.ExternalComponentView {
  return view instanceof t.ExternalComponentView;
}

interface RenderTagViewProps {
  view: t.TagView;
}

const RenderTagView = observer((props: RenderTagViewProps) => {
  const domRef = React.useRef<HTMLElement | null>(null);

  if (props.view.tag === "text") {
    return <span ref={domRef}>{props.view.props.value as string}</span>;
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- disabling unsafe assignment for props.view.props.style
  const style: Record<string, never> = props.view.props.style
    ? t.toJS(props.view.props.style)
    : {};

  let elProps = { ...props.view.props };

  /**
   * The renderer is responsible for implementing any and all relevant prop bindings associated with a HTML element
   *
   * Craft.js should implement all necessary prop bindings for HTML elements,
   * but for this demo in Reka, we will only implement the "value" prop binding for the input tag
   */
  Object.entries(props.view.bindings).forEach(([name, updater]) => {
    if (name === "value" && props.view.tag === "input") {
      elProps = {
        ...elProps,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          updater(e.target.value);
        },
      };
    }
  });

  return React.createElement(
    props.view.tag,
    {
      ...elProps,
      style,
    },
    props.view.children.length > 0
      ? props.view.children.map((child) => (
          <InternalRenderer key={child.key} view={child} />
        ))
      : undefined
  );
});

interface RendererProps {
  view: t.View;
}

const InternalRenderer = observer((props: RendererProps) => {
  const view = props.view;

  if (view instanceof t.TagView) {
    return <RenderTagView view={view} />;
  }

  if (view instanceof t.ComponentView) {
    return <RenderComponentView view={view} />;
  }

  if (view instanceof t.ErrorSystemView) {
    return <RenderErrorView view={view} />;
  }

  if (view instanceof t.FragmentView || view instanceof t.FrameView) {
    return (
      <>
        {view.children.map((child) => (
          <InternalRenderer key={child.id} view={child} />
        ))}
      </>
    );
  }

  return null;
});

export const Renderer = observer((props: RendererProps) => {
  const view = props.view;

  return <InternalRenderer view={view} />;
});

interface RenderFrameProps {
  frame: Frame;
}

export const RenderFrame = observer((props: RenderFrameProps) => {
  return <Renderer view={props.frame.view} />;
});
